import {
  Alert,
  BodyText,
  Button,
  Checkbox,
  Flex,
  Heading,
  RadioGroup,
  RadioTile,
} from '@utilitywarehouse/hearth-react';
import { FormikContextType, useFormikContext } from 'formik';
import { FC, useCallback } from 'react';

import { GetOtsMatchQuery } from '@generated/gql/graphql';
import { useOTSProviders } from '@hooks/OTS/useOTSProviders';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import {
  FindStatus,
  IOTSAddress,
  IOTSProvider,
  OrderType,
} from '@src/types/oneTouchSwitching';
import { YourDetailsFormValues } from '@src/types/yourDetails';

import { ProviderSelect } from '../ProviderSelect';
import { SwitchSuccessText } from '../SwitchSuccessText';
import { FindingDialog } from '../dialogs/FindingDialog';
import { FoundDialog } from '../dialogs/FoundDialog';
import { NotFoundDialog } from '../dialogs/NotFoundDialog';
import { useOtsMatch } from '../hooks/useOtsMatch';
import '../styles.scss';
import {
  getInitialOtsState,
  getProviderResetState,
  getValidOtsOptions,
  parseOtsFieldErrors,
  parseOtsMatchDetails,
} from '../utils';
import NoProvidersFound from './NoProvidersFound';

export interface SwitchTileProps {
  orderType: OrderType;
  currentAddress: IOTSAddress;
  numberToPort: string | null;
  setFieldValue: FormikContextType<YourDetailsFormValues>['setFieldValue'];
}

export const SwitchTile: FC<SwitchTileProps> = ({
  orderType,
  currentAddress,
  numberToPort,
  setFieldValue,
}) => {
  const { values, setValues } = useFormikContext<YourDetailsFormValues>();

  const {
    otsHasExistingLine: hasExistingLine,
    otsProviderNotListed: providerNotListed,
    otsSelectedProvider: selectedProvider,
    otsFindStatus: findStatus,
    otsComms: comms,
    otsHomePhoneSuffix: homePhoneSuffix,
    otsAccountReference: accountReference,
    otsBroadbandReference: broadbandReference,
    otsHomePhoneReference: homePhoneReference,
    otsMatchId: matchId,
    otsSwitchingReference: switchingReference,
    otsSwitchOptions: switchOptions,
    aboutNameLast: surname,
  } = values;

  const canSubmit = !!surname;

  const { canUpdateState } = useAppointmentContext();
  const providers = useOTSProviders();

  const setStatus = useCallback(
    (status: FindStatus) => {
      setValues((prev) => ({
        ...prev,
        otsFindStatus: status,
        otsFieldErrors: [],
      }));
    },
    [setValues]
  );
  const handleMatchCreated = useCallback(
    (id: string) => {
      setFieldValue('otsMatchId', id);
    },
    [setFieldValue]
  );

  const handleFail = useCallback(() => {
    setStatus(FindStatus.FAIL);
  }, [setStatus]);

  const handleNotFound = useCallback(
    (matchData: GetOtsMatchQuery) => {
      const fieldErrors = parseOtsFieldErrors(
        matchData?.broadbandOtsRetrieveMatch?.fieldErrors,
        orderType
      );
      setValues((prev) => ({
        ...prev,
        otsFindStatus: FindStatus.NOT_FOUND,
        otsFieldErrors: fieldErrors,
        otsMatchId: null,
      }));
    },
    [orderType, setValues]
  );

  const handleSuccess = useCallback(
    (matchData: GetOtsMatchQuery) => {
      const validOptions = getValidOtsOptions(matchData, orderType);

      if (!validOptions.length) {
        handleNotFound(matchData);
        return;
      }

      const { suffix, switchingReference, communications, switchOptions } =
        parseOtsMatchDetails(matchData, validOptions);

      setValues((prev) => ({
        ...prev,
        otsComms: communications,
        otsFindStatus: FindStatus.FOUND,
        otsHomePhoneSuffix: suffix,
        otsFieldErrors: [],
        otsMatchId: null,
        otsSwitchOptions: switchOptions,
        otsSwitchingReference: switchingReference,
      }));
    },
    [orderType, handleNotFound, setValues]
  );

  const handleExistingLineChange = useCallback(
    (value: string) => {
      setValues((prev) => ({
        ...prev,
        ...getInitialOtsState(),
        otsHasExistingLine: value === 'yes',
        otsProviderNotListed: false,
        otsSelectedProvider: null,
      }));
    },
    [setValues]
  );

  const handleProviderSelect = useCallback(
    (value: IOTSProvider | null) => {
      setValues((prev) => ({
        ...prev,
        ...getProviderResetState(),
        otsSelectedProvider: value,
        otsProviderNotListed: false,
      }));
    },
    [setValues]
  );

  const handleProviderNotListedChange = useCallback(
    (value: boolean) => {
      setValues((prev) => ({
        ...prev,
        ...getProviderResetState(),
        otsProviderNotListed: value,
        otsSelectedProvider: null,
      }));
    },
    [setValues]
  );

  const { findDetails } = useOtsMatch({
    selectedProvider,
    orderType,
    currentAddress,
    numberToPort,
    surname,
    accountReference,
    broadbandReference,
    homePhoneReference,
    matchId,
    canUpdateState,

    onMatchCreated: handleMatchCreated,
    onSuccess: handleSuccess,
    onNotFound: handleNotFound,
    onFail: handleFail,
  });

  return (
    <>
      <FindingDialog
        open={findStatus === FindStatus.FINDING}
        canUpdateState={canUpdateState}
      />

      <NotFoundDialog
        open={findStatus === FindStatus.NOT_FOUND}
        providers={providers.providers}
        canUpdateState={canUpdateState}
        onRetry={(updatedSurname) => {
          setStatus(FindStatus.FINDING);
          findDetails(updatedSurname);
        }}
      />

      <FoundDialog
        open={findStatus === FindStatus.FOUND}
        onContinue={() => setStatus(FindStatus.SUCCESS)}
        provider={selectedProvider}
        comms={comms}
        homePhoneSuffix={homePhoneSuffix}
        switchingReference={switchingReference}
        switchOptions={switchOptions}
        setReference={(ref: string) =>
          setFieldValue('otsSwitchingReference', ref)
        }
        canUpdateState={canUpdateState}
      />

      <Flex direction="column" spacing="lg">
        <Flex direction="column" spacing="md">
          <Heading size="md">Your broadband switch</Heading>
          <BodyText>
            To make sure you're not paying for more than one broadband service
            at the same time, we'll let your current provider know you're
            switching to us and they'll automatically cancel your billing when
            you switch.
          </BodyText>
        </Flex>

        <RadioGroup
          label={`Are you currently paying for a supply at ${currentAddress.addressLines[0]} that you would like to switch to UW?`}
          direction="row"
          className="switch-tile__radio-group"
          value={hasExistingLine ? 'yes' : 'no'}
          onValueChange={handleExistingLineChange}
        >
          <RadioTile
            label="Yes"
            value="yes"
            id="existing-line-yes"
            data-testid="existing-line-yes"
          />
          <RadioTile
            label="No"
            value="no"
            id="existing-line-no"
            data-testid="existing-line-no"
          />
        </RadioGroup>

        {hasExistingLine && (
          <>
            <ProviderSelect
              providers={providers.providers}
              selectedProvider={selectedProvider}
              setSelectedProvider={handleProviderSelect}
              disabled={providerNotListed}
              isLoading={providers.isLoading}
              onOpen={providers.retryIfNeeded}
            >
              {({ setOpen }) =>
                (providers.hasErrored || !providers.foundProviders) && (
                  <NoProvidersFound
                    isLoading={providers.isLoading}
                    onRetry={() => {
                      setOpen(true);
                      providers.refetch();
                    }}
                    onContinue={() => {
                      setOpen(false);
                      handleProviderNotListedChange(true);
                    }}
                  />
                )
              }
            </ProviderSelect>
            <Checkbox
              data-testid="otsCheckbox"
              id="otsCheckbox"
              name="otsCheckbox"
              label="My provider isn’t listed"
              checked={providerNotListed}
              onCheckedChange={handleProviderNotListedChange}
            />
            {findStatus !== FindStatus.SUCCESS && (
              <div>
                <Button
                  variant="outline"
                  colorScheme="functional"
                  type="button"
                  onClick={() => {
                    setStatus(FindStatus.FINDING);
                    findDetails();
                  }}
                  disabled={
                    !canSubmit ||
                    !hasExistingLine ||
                    providerNotListed ||
                    !selectedProvider
                  }
                >
                  Find my broadband details
                </Button>
              </div>
            )}
            {providerNotListed && (
              <Alert
                colorScheme="warning"
                text="Please check the list of providers again so we can make your switch as smooth as possible. If you can't find your provider on the list, you can still confirm your order with us, but you'll need to contact your current provider yourself to cancel your current service and understand any implications."
              />
            )}
            {findStatus === FindStatus.SUCCESS && (
              <Alert colorScheme="positive">
                <SwitchSuccessText
                  provider={selectedProvider}
                  comms={comms}
                  homePhoneSuffix={homePhoneSuffix}
                  switchingReference={switchingReference}
                  switchOptions={switchOptions}
                />
              </Alert>
            )}
            {findStatus === FindStatus.FAIL && (
              <Alert colorScheme="warning">
                We couldn't find your broadband with {selectedProvider?.name}.
                We'll be in touch after you sign up to make sure your switch is
                as smooth as possible.
              </Alert>
            )}
          </>
        )}

        {!hasExistingLine && (
          <Alert
            colorScheme="warning"
            text="Please make sure you let us know if you currently pay for a broadband supply at this address. Otherwise we can't manage the switch for you and you'll need to contact your current provider yourself to understand any terms and cancel your contract."
          />
        )}
      </Flex>
    </>
  );
};
